/**
 * Timestamps, on the clock the rest of Pipyar runs on.
 *
 * Every candle is stored shifted to the broker clock, and the charts render
 * those numbers as they are — so the axis beneath a knot reads broker time,
 * not the reader's wall clock. An alert about that knot has to agree with the
 * axis: if it does not, the two disagree by the offset and the reader is left
 * deciding which to trust.
 *
 * The API hands these out as TRUE UTC instants: every route that reports an
 * event time subtracts the broker shift first. Formatting one therefore means
 * putting the shift back, which is what formatBrokerTime does. It takes the
 * real instant and the offset in a single call deliberately — the earlier
 * two-step version made it easy to format an already-shifted value, which
 * landed a daily bar on the previous day.
 */

/** Real epoch seconds to the broker-shifted frame candles are stored in. */
export function toBrokerClock(realSec: number, storedGmtOffsetSec: number): number {
  return realSec + storedGmtOffsetSec;
}

/** HH:MM on the broker clock. The shift is already in the value, so read as UTC. */
function clockTime(brokerSec: number): string {
  return new Date(brokerSec * 1000).toLocaleTimeString([], {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * A true UTC instant as an exact date and time on the broker clock.
 *
 * Same day as now: `Today 20:00`. Naming the date of something that happened
 * an hour ago is noise, and the word reads faster than the number.
 * Any other day: `19 Oct 00:00`, with the year added only when it is not the
 * current one — on a knot from last December that is the whole point.
 *
 * "Today" is judged on the BROKER clock as well, not the reader's. The two
 * disagree for the hours either side of midnight, and through them an alert
 * would otherwise name a day the chart axis does not show.
 *
 * @param todayLabel translated "Today"; omit it and today shows just the time.
 */
export function formatBrokerTime(
  realSec: number,
  storedGmtOffsetSec: number,
  todayLabel?: string,
): string {
  const broker = toBrokerClock(realSec, storedGmtOffsetSec);
  const nowBroker = toBrokerClock(Date.now() / 1000, storedGmtOffsetSec);

  const d = new Date(broker * 1000);
  const now = new Date(nowBroker * 1000);
  const time = clockTime(broker);

  if (
    d.getUTCFullYear() === now.getUTCFullYear() &&
    d.getUTCMonth() === now.getUTCMonth() &&
    d.getUTCDate() === now.getUTCDate()
  ) {
    return todayLabel ? `${todayLabel} ${time}` : time;
  }

  const date = d.toLocaleDateString([], {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: d.getUTCFullYear() === now.getUTCFullYear() ? undefined : 'numeric',
  });
  return `${date} ${time}`;
}
